/**
 * Helpers to get user-facing messages from API error responses.
 * Backend may return detail as a string or as an object (e.g. rate limit: { message, error, retry_after }).
 * Using detail directly when it's an object causes "[object Object]" in the UI.
 */

import { isAxiosError } from 'axios';

import { ErrorMessages } from '../constants/documentMessages';
import {
  DEFAULT_USER_FACING_ERROR_MESSAGE,
  sanitizeGenericServerFailureMessage,
  toUserFacingErrorMessage,
} from './userFacingErrorMessage';

/**
 * FastAPI / Pydantic validation errors use detail: [{ loc, msg, type }, ...].
 */
function getFastApiValidationDetailMessages(detail: unknown): string | undefined {
  if (!Array.isArray(detail) || detail.length === 0) return undefined;
  const msgs: string[] = [];
  for (const item of detail) {
    if (item != null && typeof item === 'object' && 'msg' in item) {
      const m = (item as { msg?: unknown }).msg;
      if (typeof m === 'string' && m.trim()) msgs.push(m.trim());
    }
  }
  if (msgs.length === 0) return undefined;
  return [...new Set(msgs)].join(' ');
}

/**
 * Get a user-facing message from response.data (or any object with a detail field).
 * Handles detail as string or as object with message/error.
 */
export function getApiErrorMessage(data: unknown): string | undefined {
  if (data == null || typeof data !== 'object') return undefined;
  const topLevel = (data as { message?: unknown }).message;
  if (typeof topLevel === 'string' && topLevel.trim()) {
    return sanitizeGenericServerFailureMessage(topLevel.trim(), DEFAULT_USER_FACING_ERROR_MESSAGE);
  }
  const d = (data as { detail?: unknown }).detail;
  if (typeof d === 'string') {
    return sanitizeGenericServerFailureMessage(d, DEFAULT_USER_FACING_ERROR_MESSAGE);
  }
  if (Array.isArray(d)) {
    const fromValidation = getFastApiValidationDetailMessages(d);
    if (fromValidation) {
      return sanitizeGenericServerFailureMessage(fromValidation, DEFAULT_USER_FACING_ERROR_MESSAGE);
    }
  }
  if (d != null && typeof d === 'object') {
    const obj = d as { message?: string; error?: string };
    if (typeof obj.message === 'string') {
      return sanitizeGenericServerFailureMessage(obj.message, DEFAULT_USER_FACING_ERROR_MESSAGE);
    }
    if (typeof obj.error === 'string') {
      return sanitizeGenericServerFailureMessage(obj.error, DEFAULT_USER_FACING_ERROR_MESSAGE);
    }
  }
  return undefined;
}

/**
 * Get a stable error code from response.data when detail is an object (e.g. { message, code }).
 * Lets API consumers distinguish cases like invitation_expired (400) vs invitation_invalid (404).
 */
export function getApiErrorCode(data: unknown): string | undefined {
  if (data == null || typeof data !== 'object') return undefined;
  const d = (data as { detail?: unknown }).detail;
  if (d != null && typeof d === 'object' && 'code' in d) {
    const code = (d as { code?: string }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/** Backend may set `detail.code` on 429 when the limit is quota/plan, not a transient rate cap. */
export const QUOTA_EXCEEDED_ERROR_CODE = 'quota_exceeded';

function getHttpErrorResponse(error: unknown): { status?: number; data?: unknown } | undefined {
  if (isAxiosError(error)) return error.response;
  if (error == null || typeof error !== 'object') return undefined;
  const response = (error as { response?: unknown }).response;
  if (response == null || typeof response !== 'object') return undefined;
  const r = response as { status?: unknown; data?: unknown };
  return {
    status: typeof r.status === 'number' ? r.status : undefined,
    data: r.data,
  };
}

function looksLikeGenericRateLimitCopy(message: string): boolean {
  const t = message.toLowerCase();
  return (
    /\btoo many requests\b/.test(t) ||
    /\brate limit(ed)?\b/.test(t) ||
    /\btry again (later|shortly|in a few|soon)\b/.test(t) ||
    /\bplease wait\b/.test(t)
  );
}

function collectDetailTextForQuotaHeuristic(data: unknown): string {
  if (data == null || typeof data !== 'object') return '';
  const parts: string[] = [];
  const msg = getApiErrorMessage(data);
  if (msg) parts.push(msg);
  const d = (data as { detail?: unknown }).detail;
  if (typeof d === 'string') parts.push(d);
  if (d != null && typeof d === 'object' && !Array.isArray(d)) {
    const o = d as Record<string, unknown>;
    for (const k of ['message', 'error', 'reason', 'description'] as const) {
      const v = o[k];
      if (typeof v === 'string') parts.push(v);
    }
  }
  return parts.join(' ').toLowerCase();
}

/**
 * True when a 429 should be treated as plan/storage quota (do not retry like a rate limit).
 * Prefers `detail.code === quota_exceeded` when present; otherwise uses conservative text heuristics on the response body.
 */
export function isQuotaExceeded429Error(error: unknown): boolean {
  const response = getHttpErrorResponse(error);
  if (response?.status !== 429) return false;
  const data = response.data;
  const code = getApiErrorCode(data)?.toLowerCase();
  if (code === QUOTA_EXCEEDED_ERROR_CODE || code?.includes('quota_exceeded')) return true;
  if (code?.includes('quota') && code.includes('exceed')) return true;

  const haystack = collectDetailTextForQuotaHeuristic(data);
  if (!haystack) return false;

  if (/\bquota\b/.test(haystack) && /\b(exceed|exceeded|limit|full|capacity)\b/.test(haystack))
    return true;
  if (/\bstorage\b/.test(haystack) && /\b(quota|limit|exceed|exceeded|plan)\b/.test(haystack))
    return true;
  if (/\bmonthly\b/.test(haystack) && /\b(storage|quota|limit)\b/.test(haystack)) return true;
  if (/\bplan\b/.test(haystack) && /\b(limit|quota|storage|exceed|exceeded)\b/.test(haystack))
    return true;
  if (/\bbilling (period|cycle)\b/.test(haystack)) return true;
  return false;
}

/**
 * User-facing copy for HTTP 429: quota/plan vs transient rate limit.
 */
export function resolveHttp429UserMessage(error: unknown): string {
  const response = getHttpErrorResponse(error);
  if (response?.status !== 429) {
    return ErrorMessages.system.tooManyRequests;
  }
  const data = response.data;
  const fromApi = data != null && typeof data === 'object' ? getApiErrorMessage(data) : undefined;

  if (isQuotaExceeded429Error(error)) {
    if (fromApi && !looksLikeGenericRateLimitCopy(fromApi)) return fromApi;
    return ErrorMessages.system.quotaExceededGuidance;
  }

  return fromApi || ErrorMessages.system.tooManyRequests;
}

/**
 * Message for file upload (and similar multipart) failures when the client only has an Axios error.
 * Prefer JSON `detail` from the API when present; map gateway (502/503/504) and network/timeouts
 * to friendly copy instead of Axios's "Request failed with status code …".
 */
export function getUserFacingUploadRequestErrorMessage(error: unknown): string {
  const fallback = ErrorMessages.system.uploadFailed;

  if (isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data;

    if (status === 429) {
      return resolveHttp429UserMessage(error);
    }

    if (data != null && typeof data === 'object') {
      const fromApi = getApiErrorMessage(data);
      if (fromApi) return fromApi;
    }

    if (status != null && status >= 500) {
      return ErrorMessages.system.serviceDisruption;
    }

    if (!error.response) {
      if (error.code === 'ECONNABORTED' || /\btimeout\b/i.test(error.message)) {
        return ErrorMessages.system.uploadTimeout;
      }
      if (error.message === 'Network Error') {
        return ErrorMessages.system.networkError;
      }
    }

    return toUserFacingErrorMessage(error.message || '', fallback);
  }

  if (error instanceof Error) {
    return toUserFacingErrorMessage(error.message, fallback);
  }

  if (typeof error === 'string') {
    return toUserFacingErrorMessage(error, fallback);
  }

  return fallback;
}

/**
 * Get tracing request id from response.data when the API includes it (e.g. error JSON with request_id).
 */
export function getApiErrorRequestId(data: unknown): string | undefined {
  if (data == null || typeof data !== 'object') return undefined;
  const o = data as { request_id?: unknown; requestId?: unknown };
  const id = o.request_id ?? o.requestId;
  if (typeof id === 'string') {
    const t = id.trim();
    return t.length > 0 ? t : undefined;
  }
  return undefined;
}
